import { useState } from "react";
import type { AdminGalleryAlbum, AdminGalleryPhoto } from "@tih/shared";
import { AdminPosterField } from "../AdminPosterField";
import { GalleryAlbumUploadPanel } from "../GalleryAlbumUploadPanel";
import { adminUpdateGalleryAlbum } from "../../../api/client";

type GalleryAlbumEditorProps = {
  album: AdminGalleryAlbum;
  photos: AdminGalleryPhoto[];
  photosLoading: boolean;
  zipDownloadActive: boolean;
  onUpdate: (patch: Parameters<typeof adminUpdateGalleryAlbum>[1]) => void;
  onDelete: () => void;
  onDownloadZip: () => void;
  onError: (message: string | null) => void;
  onRemovePhoto: (photoId: number) => void;
};

function slugifyAlbumUrl(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function GalleryAlbumEditor({
  album,
  photos,
  photosLoading,
  zipDownloadActive,
  onUpdate,
  onDelete,
  onDownloadZip,
  onError,
  onRemovePhoto,
}: GalleryAlbumEditorProps) {
  const [photosModalOpen, setPhotosModalOpen] = useState(false);

  return (
    <div className="admin-gallery-album-editor">
      <div className="admin-gallery-album-toolbar">
        <a
          href={`/gallery/${album.slug}`}
          target="_blank"
          rel="noreferrer"
          className="btn btn-ghost"
        >
          Preview
        </a>
        <button
          type="button"
          className={album.published ? "btn btn-ghost" : "btn btn-primary"}
          onClick={() => onUpdate({ published: !album.published })}
        >
          {album.published ? "Unpublish" : "Publish"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setPhotosModalOpen(true)}
        >
          {photos.length === 0 ? "Add photos" : "Manage photos"}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={photosLoading || photos.length === 0 || zipDownloadActive}
          onClick={onDownloadZip}
        >
          Download zip
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDelete}>
          Delete album
        </button>
      </div>

      <label className="admin-field admin-gallery-url-field">
        <span>Album URL</span>
        <div className="admin-gallery-url-input">
          <span className="admin-gallery-url-prefix">/gallery/</span>
          <input
            key={album.slug}
            defaultValue={album.slug}
            spellCheck={false}
            autoCapitalize="off"
            onBlur={(e) => {
              const v = slugifyAlbumUrl(e.target.value.trim());
              e.target.value = v;
              if (v && v !== album.slug) onUpdate({ slug: v });
            }}
          />
        </div>
      </label>

      <div className="admin-card-row">
        <label className="admin-field">
          <span>Album title</span>
          <input
            defaultValue={album.title}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== album.title) onUpdate({ title: v });
            }}
          />
        </label>
        <label className="admin-field">
          <span>Caption</span>
          <input
            defaultValue={album.caption ?? ""}
            onBlur={(e) => {
              const v = e.target.value.trim() || null;
              if (v !== (album.caption ?? null)) onUpdate({ caption: v });
            }}
          />
        </label>
      </div>

      <AdminPosterField
        value={album.coverUrl ?? ""}
        onChange={(url) => onUpdate({ coverUrl: url || null })}
        formIdentifier="admin-gallery-cover"
        previewSrc={album.coverUrl}
      />

      <GalleryAlbumUploadPanel
        album={album}
        photos={photos}
        photosLoading={photosLoading}
        modalOpen={photosModalOpen}
        onModalOpenChange={setPhotosModalOpen}
        onUpdate={onUpdate}
        onRemovePhoto={onRemovePhoto}
        onError={onError}
      />
    </div>
  );
}
